import { Card, CardContent, CardHeader, Fab, Grow, IconButton } from "@mui/material";
import { Close as CloseIcon, ListAlt as ListAltIcon } from "@mui/icons-material";
import { useAtom } from "jotai";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import clsx from "clsx";

import { activeOverlayersAtom } from "src/atoms/overlayers";
import { OverlayersLegendItem } from "./OverlayersLegendItem";
import { filterActive, filterByContext } from "src/utils/layer-manager.utils";
import type { LayerManagerContext, Overlayer } from "src/types";
import { orderBy } from "lodash";
import { pipe } from "src/utils/fp.utils";
import { isComponentLegend } from "src/utils/legend.utils";

interface Props {
  context: LayerManagerContext;
}

export function OverlayersLegend(props: Props) {
  const [activeLayers] = useAtom(activeOverlayersAtom);

  const [isOpen, setIsOpen] = useState(true);
  const [backLegendKey, setBackLegendKey] = useState<Overlayer["key"] | null>(null);
  const { t } = useTranslation();

  const overlayers = useMemo((): Overlayer[] => {
    const sort = (overlayers: Overlayer[]) => orderBy(overlayers, "zIndex", "desc");

    return pipe(filterActive, filterByContext(props.context), sort)(activeLayers);
  }, [activeLayers, props.context]);

  function toggleBackLegend(layer: Overlayer) {
    if (backLegendKey === layer.key) setBackLegendKey(null);
    else setBackLegendKey(layer.key);
  }

  return render();

  function render() {
    return (
      <Styles>
        {renderOpen()}
        {renderClosed()}
      </Styles>
    );
  }

  function renderClosed() {
    return (
      <Grow in={!isOpen} style={{ transformOrigin: "bottom left" }} mountOnEnter unmountOnExit>
        <Fab size="medium" onClick={(e) => setIsOpen(!isOpen)} color="default" className="open-btn">
          <ListAltIcon />
        </Fab>
      </Grow>
    );
  }

  function renderOpen() {
    return (
      <Grow in={isOpen} mountOnEnter unmountOnExit>
        <div>
          <div className="legend-front">
            <Card>
              <CardHeader
                action={
                  <IconButton onClick={() => setIsOpen(false)} size="large">
                    <CloseIcon fontSize="small" />
                  </IconButton>
                }
                title={t("label.overlayersLegend")}
              />
              <CardContent className="card-content-wrapper">
                <div className="card-content">
                  {overlayers.map((layer) => (
                    <OverlayersLegendItem key={layer.key} layer={layer} onEdit={() => toggleBackLegend(layer)} />
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Back legend */}
          {overlayers.map((layer) => {
            if (isComponentLegend(layer.legend) && layer.legend.componentBack) {
              return (
                <div key={layer.key} className={clsx("legend-back", { active: backLegendKey === layer.key })}>
                  <layer.legend.componentBack />
                </div>
              );
            } else return null;
          })}
        </div>
      </Grow>
    );
  }
}

const Styles = styled.div`
  .MuiCard-root {
    display: flex;
    flex-direction: column;
    position: relative;
    width: 100%;
    height: 100%;
    background-color: white;
  }

  .MuiCardHeader-root {
    padding-bottom: ${({ theme }) => theme.spacing(1)};
    transform: translateY(-4px);
  }

  .MuiCardHeader-action {
    margin-left: 8px;
  }

  .card-content-wrapper {
    padding: 0;
    overflow: hidden;
    background-color: white;
    border: 0;
  }

  .card-content {
    padding: 0;
    max-height: 300px;
    overflow: auto;
  }

  .open-btn {
    position: absolute;
    bottom: 0;
  }

  .legend-front,
  .legend-back {
    background-color: white;
    width: auto;
    height: auto;
    z-index: 10;
  }

  .legend-front {
    position: relative;
    min-width: 210px;
    min-height: 340px;
    height: 380px;
  }

  .legend-back {
    position: absolute;
    z-index: 1;
    transition: all 300ms;
    width: 100%;
    height: 100%;
    left: 0%;
    top: 0;

    &.active {
      left: 100%;
    }
  }
`;
